/**
 * Custom mega-menu panel that attaches to a top-level header menu item by key.
 *
 * The block renders <mega-menu-feature data-mega-menu-key="covers"> ... panel ... </mega-menu-feature>
 * inside <header-component>. This controller finds the top-level menu link whose
 * (handleized) title matches the key and shows the panel as that item's dropdown
 * on hover/focus.
 *
 * Robustness notes:
 * - The theme re-renders the header (section hydration, cart updates), which can
 *   replace the menu-link nodes. A MutationObserver re-applies the chevron and
 *   marker so the cue never disappears after a re-render.
 * - Hover/focus is handled with delegated listeners on the header, gated by a
 *   per-instance ownership marker, so it survives node replacement and two blocks
 *   never both open on the same item.
 */

let instanceSeq = 0;

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
const normalizeKey = (value) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/['’"]/g, '')
    .replace(/&amp;/g, 'and')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CHEVRON_SVG =
  '<svg viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class MegaMenuFeature extends HTMLElement {
  /** @type {string} */ key = '';
  /** @type {string} */ uid = '';
  /** @type {number} */ closeDelay = 300;
  /** @type {boolean} */ isOpen = false;
  /** @type {HTMLElement | null} */ header = null;
  /** @type {Element | null} */ trigger = null;
  /** @type {Element | null} */ item = null;
  /** @type {Element | null} */ chevron = null;
  /** @type {ReturnType<typeof setTimeout> | null} */ closeTimer = null;
  /** @type {boolean} */ decorateQueued = false;
  /** @type {AbortController | null} */ abortController = null;
  /** @type {MutationObserver | null} */ observer = null;

  connectedCallback() {
    this.key = normalizeKey(this.dataset.megaMenuKey);
    this.closeDelay = parseInt(this.dataset.closeDelay || '300', 10);
    if (!this.key) return;

    const header = this.closest('header-component') || document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;
    this.header = header;

    this.uid = `mmf-${(instanceSeq += 1)}`;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Delegated hover/focus on the header — survives menu node replacement.
    header.addEventListener('pointerover', (e) => this.#onHeaderPointerOver(e), { signal });
    header.addEventListener('pointerout', (e) => this.#onHeaderPointerOut(e), { signal });
    header.addEventListener('focusin', (e) => this.#onHeaderFocusIn(e), { signal });

    // Keep the panel open only while the pointer is over the visible card (the
    // panel itself is click-through), so leaving the card in any direction closes.
    const card = this.querySelector('.mmf__card');
    if (card) {
      card.addEventListener('pointerenter', () => this.#cancelClose(), { signal });
      card.addEventListener('pointerleave', () => this.#scheduleClose(), { signal });
    }
    // Re-align under the trigger if the layout changes while open.
    window.addEventListener(
      'resize',
      () => {
        if (this.isOpen) this.#position();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') this.close();
      },
      { signal }
    );

    // Decorate now, and re-decorate whenever the header DOM changes (re-renders).
    this.#decorate();
    this.observer = new MutationObserver(() => this.#scheduleDecorate());
    this.observer.observe(header, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.#cancelClose();
  }

  /** Coalesce re-decoration to once per frame to avoid thrashing during re-renders. */
  #scheduleDecorate() {
    if (this.decorateQueued) return;
    this.decorateQueued = true;
    requestAnimationFrame(() => {
      this.decorateQueued = false;
      this.#decorate();
    });
  }

  /**
   * Find the matching top-level link and ensure it owns the chevron + marker.
   * Idempotent and safe to call repeatedly.
   */
  #decorate() {
    const trigger = this.#findTrigger();
    if (!trigger) return;

    const owner = trigger.getAttribute('data-mmf-owner');
    if (owner && owner !== this.uid) return; // another panel owns this item
    trigger.setAttribute('data-mmf-owner', this.uid);

    this.trigger = trigger;
    this.item = trigger.closest('.menu-list__list-item');

    // Replace the item's native dropdown chrome with our panel: remove its native
    // submenu and popup affordances so the theme renders no submenu background.
    const nativeSubmenu = this.item && this.item.querySelector(':scope > .menu-list__submenu');
    if (nativeSubmenu) nativeSubmenu.remove();
    trigger.removeAttribute('aria-haspopup');
    trigger.removeAttribute('aria-controls');

    // Ensure the chevron exists (re-inject if a re-render removed it).
    if (!trigger.querySelector('.mmf-chevron')) {
      trigger.classList.add('mmf-has-megamenu');
      const chevron = document.createElement('span');
      chevron.className = 'mmf-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = CHEVRON_SVG;
      trigger.appendChild(chevron);
    }
    this.chevron = trigger.querySelector('.mmf-chevron');

    // Keep the chevron + aria state in sync with the panel.
    this.chevron?.classList.toggle('is-open', this.isOpen);
    trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
  }

  /** @returns {Element | null} */
  #findTrigger() {
    const links = this.header?.querySelectorAll('.menu-list__link');
    if (!links) return null;
    for (const link of links) {
      const titleEl = link.querySelector('.menu-list__link-title') || link;
      if (normalizeKey(titleEl.textContent) === this.key) return link;
    }
    return null;
  }

  /**
   * Is the given event target part of the menu item this panel owns?
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #ownsTarget(target) {
    const el = target instanceof Element ? target : null;
    const link = el?.closest('.menu-list__link');
    return !!link && link.getAttribute('data-mmf-owner') === this.uid;
  }

  /** @param {PointerEvent} e */
  #onHeaderPointerOver(e) {
    if (this.#ownsTarget(e.target)) this.open();
  }

  /** @param {PointerEvent} e */
  #onHeaderPointerOut(e) {
    if (!this.#ownsTarget(e.target)) return;
    // Don't close if moving into the panel or staying within the trigger.
    const to = e.relatedTarget instanceof Node ? e.relatedTarget : null;
    if (to && (this.contains(to) || (this.trigger?.contains(to) ?? false))) return;
    this.#scheduleClose();
  }

  /** @param {FocusEvent} e */
  #onHeaderFocusIn(e) {
    const node = e.target instanceof Node ? e.target : null;
    if (this.#ownsTarget(e.target) || (node !== null && this.contains(node))) this.open();
    else this.#scheduleClose();
  }

  open() {
    this.#cancelClose();
    this.isOpen = true;
    this.#position();
    this.classList.add('is-open');
    this.chevron?.classList.add('is-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  /**
   * Align the panel card's left edge under the trigger menu item, clamped so the
   * card never runs off the right edge of the header.
   */
  #position() {
    if (!this.header || !this.trigger) return;
    const headerRect = this.header.getBoundingClientRect();
    const triggerRect = this.trigger.getBoundingClientRect();
    let left = triggerRect.left - headerRect.left;
    const card = this.querySelector('.mmf__card');
    if (card instanceof HTMLElement) {
      const maxLeft = headerRect.width - card.offsetWidth;
      if (maxLeft > 0 && left > maxLeft) left = maxLeft;
    }
    if (left < 0) left = 0;
    this.style.setProperty('--mmf-left', `${left}px`);
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.chevron?.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  #scheduleClose() {
    this.#cancelClose();
    this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
  }

  #cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

if (!customElements.get('mega-menu-feature')) {
  customElements.define('mega-menu-feature', MegaMenuFeature);
}
